import {
    LoadingBar,
    SelectableTreeMenu,
    SelectableTreeMenuItem,
    Stack,
} from "@biggeo/bg-ui/lab";
import * as A from "fp-ts/Array";
import { pipe } from "fp-ts/lib/function";

import {
    SavedArea,
    useFetchAreasExtendedQuery,
    useFetchSavedAreasByIdsLazyQuery,
} from "@biggeo/bg-server-lib/studio";
import { useEffect } from "react";
import { convertSlimSavedAreas } from "../utils/utils";
import { match } from "@vividtheory/remotedata";

import { useMap } from "../mapbox/context";
import { getPolygonsFromSavedAreas } from "../utils/geometry-utils";
import { MapContextPolygon } from "../mapbox/context/types";
import { useErrorToast } from "../../toaster/containers/redux/hooks";
import {
    useMapStateUtilities,
    useSavedBoundaries,
} from "../mapbox/context/hooks";

export const SavedAreasSubmenu = ({
    mapTemplateId,
    savedAreaId,
    isSavedViewPage,
}: {
    readonly savedAreaId?: string;
    readonly mapTemplateId: string;
    readonly isSavedViewPage: boolean;
}) => {
    const errorToast = useErrorToast();

    const { selectBoundaries, unselectBoundaries } = useSavedBoundaries({
        recenter: !isSavedViewPage,
        style: false,
    });
    const { polygons } = useMap();
    const { setLoading } = useMapStateUtilities();

    // Fetch all boundaries without their geometries
    const { remote } = useFetchAreasExtendedQuery({
        variables: {
            input: {
                fkMapTemplateId: mapTemplateId,
                isSavedViewArea: false,
                skipGeometries: true,
            },
        },
    });

    // Fetch selected boundaries with their geometries
    const { executeQuery: fetchSavedAreasWithGeometries } =
        useFetchSavedAreasByIdsLazyQuery();

    // Scenario 1: Setting the Boundary when it's selected
    const handleClick = async ({
        id,
        type,
        areas,
        state,
    }: {
        id: string;
        type: "item" | "subItem";
        areas?: readonly SavedArea[];
        state: MapContextPolygon[];
    }) => {
        const boundary = state.find((p) => p.id === id);

        // 1. Unselect boundary if already selected

        // 1.1. Unselect sub boundary. e.g: Alberta
        if (boundary && type === "subItem") {
            unselectBoundaries([id]);
        }

        const isAreaSelected =
            areas &&
            areas.length > 0 &&
            areas.every((a) => !!state.map((p) => p.id).includes(a.id));

        // 1.2. Unselect main boundary. e.g: Canada
        if (isAreaSelected && type === "item") {
            unselectBoundaries(areas.map((s) => s.id));
        }

        // 2. Select either sub or main boundary
        if (
            (!boundary && type === "subItem") ||
            (!isAreaSelected && type === "item")
        ) {
            const ids = areas ? areas.map((s) => s.id) : [id];

            setLoading({ boundaries: true });

            await fetchSavedAreasWithGeometries({
                variables: { ids, fkMapTemplateId: mapTemplateId },
                onCompleted: (data) => {
                    const savedAreasWithGeometries = data.fetchSavedAreasByIds;

                    const found = savedAreasWithGeometries.filter(
                        (i) => !!ids.includes(i.id)
                    );

                    if (found.length > 0) {
                        selectBoundaries({
                            boundaries: getPolygonsFromSavedAreas(found),
                        });
                    }

                    setLoading({ boundaries: false });
                },
                onError: (error) => {
                    errorToast(
                        `An error occurred while selecting Boundary: ${JSON.stringify(error.message)}. Please try again later. `
                    );
                    setLoading({ boundaries: false });
                },
            });
        }
    };

    // Scenario 2: Setting the Boundary when it's selected from `Configuration` tab
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (savedAreaId) {
            handleClick({ id: savedAreaId, type: "subItem", state: polygons });
        }
    }, [savedAreaId]);

    const updateSelectableTreeMenuItems = (input: {
        items: SelectableTreeMenuItem[];
        polygons: MapContextPolygon[];
        selectedBoundaryId?: string;
    }): SelectableTreeMenuItem[] => {
        const { items, polygons, selectedBoundaryId } = input;

        // Finds the selected boundaries from Polygons state
        const selectedIds = new Set(
            polygons
                .map((p) => p.feature.properties?.fkSavedAreaId ?? p.id)
                .concat(selectedBoundaryId || [])
        );

        // Loops through the subItems recursively in order to update the `selected` prop
        const recurse = (
            nodes: SelectableTreeMenuItem[]
        ): SelectableTreeMenuItem[] =>
            nodes.map((node) => {
                const selected = selectedIds.has(node.id);

                return {
                    ...node,
                    selected,
                    subItems: node.subItems
                        ? recurse(node.subItems)
                        : undefined,
                };
            });

        return recurse(items);
    };

    return match(remote, {
        _: () => <LoadingBar />,
        Success: (data) => {
            const items = updateSelectableTreeMenuItems({
                items: convertSlimSavedAreas(
                    data.fetchAreasExtended || [],
                    savedAreaId
                ),
                polygons,
                selectedBoundaryId: savedAreaId,
            });

            return (
                <Stack width="100%">
                    {pipe(
                        items,
                        A.map((item) => {
                            if (!item.subItems) return [];

                            const isOpen =
                                !!savedAreaId &&
                                item.subItems
                                    ?.map((subItem) => subItem.id)
                                    .includes(savedAreaId);

                            const area = data.fetchAreasExtended.find(
                                (d) => d.area.id === item.id
                            );
                            const savedAreas = area?.savedAreas || [];

                            const isAreaSelected =
                                savedAreas.length > 0 &&
                                savedAreas.every((a) =>
                                    polygons.map((p) => p.id).includes(a.id)
                                );

                            return (
                                <SelectableTreeMenu
                                    key={item.id}
                                    showDivider
                                    menuItem={{
                                        ...item,
                                        selected: isAreaSelected,
                                    }}
                                    open={isOpen}
                                    hideCheckBox={item.hideCheckBox}
                                    onClick={async ({ id, type }) => {
                                        const areas = data.fetchAreasExtended;
                                        const area =
                                            areas && type === "item"
                                                ? areas.find(
                                                      (i) => i.area.id === id
                                                  )
                                                : undefined;

                                        await handleClick({
                                            id,
                                            type,
                                            areas: area
                                                ? area.savedAreas
                                                : undefined,
                                            state: polygons,
                                        });
                                    }}
                                />
                            );
                        })
                    )}
                </Stack>
            );
        },
    });
};
